import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, X, Trash2, Upload, FolderOpen, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import MediaPicker from "@/components/admin/MediaPicker";
import { ConfirmModal } from "@/pages/admin/AdminPortfolio";

const EMPTY = {
  title: "", slug: "", excerpt: "", featured_image: "", category: "Journal",
  tags: [], author: "JARE", body: "", status: "draft", published_at: "",
  seo_title: "", seo_description: "", social_image: "",
};

export default function BlogForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState("");
  const [pickerFor, setPickerFor] = useState(null); // "featured" | "social"
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/admin/blog/${id}`).then((r) => {
      const d = r.data;
      setForm({ ...EMPTY, ...d, published_at: (d.published_at || "").slice(0, 10) });
      setSlugTouched(true);
    }).catch(() => {
      toast.error("Post not found");
      navigate("/admin/blog");
    });
  }, [id, isEdit, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onTitle = (v) => {
    set("title", v);
    if (!slugTouched) set("slug", v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
  };

  const addTag = () => {
    const t = tagInput.trim().replace(/^#/, "");
    if (t && !form.tags.includes(t)) set("tags", [...form.tags, t]);
    setTagInput("");
  };

  const uploadImage = async (file, target) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("files", file);
    try {
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data[0]) set(target, data[0].url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const save = async (status) => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(status);
    const payload = { ...form, status, published_at: form.published_at ? new Date(form.published_at).toISOString() : null };
    try {
      if (isEdit) {
        await api.put(`/admin/blog/${id}`, payload);
      } else {
        const { data } = await api.post("/admin/blog", payload);
        toast.success(status === "published" ? "Post published" : "Draft saved");
        navigate(`/admin/blog/${data.id}/edit`, { replace: true });
        setSaving("");
        return;
      }
      toast.success(status === "published" ? "Post published" : "Draft saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSaving("");
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/blog/${id}`);
      toast.success("Post deleted");
      navigate("/admin/blog");
    } catch {
      toast.error("Could not delete post");
    }
  };

  const ImageField = ({ field, label, testid }) => (
    <div>
      <label className="label-admin">{label}</label>
      {form[field] ? (
        <div className="relative overflow-hidden rounded-xl border border-white/10">
          <img src={mediaUrl(form[field])} alt={label} className="aspect-video w-full object-cover" data-testid={`${testid}-preview`} />
          <button type="button" onClick={() => set(field, "")} aria-label={`Remove ${label}`} data-testid={`${testid}-remove`} className="glass-strong absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full text-neutral-300 hover:text-red-300">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div className="flex gap-2">
          <label className="btn-ghost flex-1 !py-2.5 text-xs cursor-pointer" data-testid={`${testid}-upload`}>
            <Upload className="h-4 w-4" /> Upload
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files[0], field)} />
          </label>
          <button type="button" onClick={() => setPickerFor(field)} className="btn-ghost flex-1 !py-2.5 text-xs" data-testid={`${testid}-library`}>
            <FolderOpen className="h-4 w-4" /> Library
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="mx-auto max-w-4xl pb-28" data-testid="blog-form-page">
      <div className="mb-8 flex items-center gap-4">
        <Link to="/admin/blog" aria-label="Back" data-testid="blog-form-back" className="glass flex h-10 w-10 items-center justify-center rounded-full text-neutral-400 hover:text-white">
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <h1 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
          {isEdit ? "Edit Post" : "New Post"}
        </h1>
      </div>

      <section className="glass rounded-3xl p-6 md:p-8">
        <p className="eyebrow mb-6">Post</p>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="label-admin" htmlFor="bf-title">Title</label>
            <input id="bf-title" value={form.title} onChange={(e) => onTitle(e.target.value)} className="input-glass" data-testid="blog-title-input" />
          </div>
          <div>
            <label className="label-admin" htmlFor="bf-slug">Slug</label>
            <input id="bf-slug" value={form.slug} onChange={(e) => { setSlugTouched(true); set("slug", e.target.value); }} className="input-glass font-mono" data-testid="blog-slug-input" />
          </div>
          <div>
            <label className="label-admin" htmlFor="bf-category">Category</label>
            <input id="bf-category" value={form.category} onChange={(e) => set("category", e.target.value)} className="input-glass" placeholder="Design Philosophy" data-testid="blog-category-input" />
          </div>
          <div className="md:col-span-2">
            <label className="label-admin" htmlFor="bf-excerpt">Excerpt</label>
            <textarea id="bf-excerpt" value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={2} className="input-glass resize-none" data-testid="blog-excerpt-input" />
          </div>
          <div>
            <label className="label-admin" htmlFor="bf-author">Author</label>
            <input id="bf-author" value={form.author} onChange={(e) => set("author", e.target.value)} className="input-glass" data-testid="blog-author-input" />
          </div>
          <div>
            <label className="label-admin" htmlFor="bf-date">Publish Date</label>
            <input id="bf-date" type="date" value={form.published_at} onChange={(e) => set("published_at", e.target.value)} className="input-glass" data-testid="blog-date-input" />
          </div>
          <div className="md:col-span-2">
            <label className="label-admin">Tags</label>
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-2.5" data-testid="blog-tags-input">
              {form.tags.map((t) => (
                <span key={t} className="flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.05] px-3 py-1.5 text-xs text-white">
                  #{t}
                  <button type="button" onClick={() => set("tags", form.tags.filter((x) => x !== t))} aria-label={`Remove ${t}`} className="text-neutral-500 hover:text-white">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder="Add tag + Enter"
                className="min-w-[120px] flex-1 bg-transparent px-2 py-1.5 text-sm text-white outline-none placeholder-neutral-500"
                data-testid="blog-tags-text"
              />
            </div>
          </div>
          <ImageField field="featured_image" label="Featured Image" testid="blog-featured" />
          <ImageField field="social_image" label="Social Share Image (optional)" testid="blog-social" />
        </div>
      </section>

      <section className="glass mt-6 rounded-3xl p-6 md:p-8">
        <p className="eyebrow mb-6">Body</p>
        <RichTextEditor value={form.body} onChange={(v) => set("body", v)} testid="blog-body-editor" />
      </section>

      <section className="glass mt-6 rounded-3xl p-6 md:p-8">
        <p className="eyebrow mb-6">SEO (optional)</p>
        <div className="grid grid-cols-1 gap-5">
          <div>
            <label className="label-admin" htmlFor="bf-seo-title">SEO Title</label>
            <input id="bf-seo-title" value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} className="input-glass" data-testid="blog-seo-title" />
          </div>
          <div>
            <label className="label-admin" htmlFor="bf-seo-desc">SEO Description</label>
            <textarea id="bf-seo-desc" value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} rows={2} className="input-glass resize-none" data-testid="blog-seo-desc" />
          </div>
        </div>
      </section>

      <div className="glass-strong sticky bottom-4 z-30 mt-8 flex flex-wrap items-center gap-3 rounded-full px-5 py-3" data-testid="blog-form-actions">
        <button type="button" onClick={() => save("draft")} disabled={Boolean(saving)} className="btn-ghost !py-2.5 text-xs disabled:opacity-50" data-testid="blog-save-draft">
          {saving === "draft" ? "Saving…" : "Save Draft"}
        </button>
        <button type="button" onClick={() => save("published")} disabled={Boolean(saving)} className="btn-primary !py-2.5 text-xs disabled:opacity-50" data-testid="blog-publish">
          {saving === "published" ? "Publishing…" : form.status === "published" ? "Update" : "Publish"}
        </button>
        {isEdit && form.slug && (
          <a href={`/blog/${form.slug}`} target="_blank" rel="noopener noreferrer" className="btn-ghost !py-2.5 text-xs" data-testid="blog-preview">
            <ExternalLink className="h-4 w-4" /> Preview
          </a>
        )}
        {isEdit && (
          <button type="button" onClick={() => setConfirmDelete(true)} className="ml-auto inline-flex items-center gap-2 rounded-full border border-red-500/30 px-5 py-2.5 text-xs font-medium text-red-300 transition-colors hover:bg-red-500/10" data-testid="blog-delete">
            <Trash2 className="h-4 w-4" /> Delete
          </button>
        )}
      </div>

      <MediaPicker open={Boolean(pickerFor)} onClose={() => setPickerFor(null)} onSelect={(m) => { set(pickerFor, m.url); setPickerFor(null); }} />
      {confirmDelete && <ConfirmModal target={form} noun="post" onCancel={() => setConfirmDelete(false)} onConfirm={doDelete} />}
    </div>
  );
}
